import { PropsWithChildren, createContext, useContext, useEffect, useState } from "react"
import { FormContext, FormContextType, FormItemContext } from "../../contexts/FormContext"
import Flex from "../Flex"

/**
 * Shared form primitives, lifted from the bespoke system in HomePage.tsx so the
 * translation request modal does not need a third copy of it.
 *
 * `prefix` selects which already-shipped stylesheet the fields inherit:
 * "contactus" (desktop, HomePage.scss) or "mb-contactus" (mobile). Callers that
 * need different sizing override width inside their own scope.
 */
const PrefixContext = createContext<string>("contactus")

export interface FormInstance extends FormContextType {
    finish: boolean
    data: Record<string, any>
}

export const useFormInstance = (): FormInstance => {

    const [finish, setFinish] = useState<boolean>(() => false)
    const [data, setData] = useState<Record<string, any>>(() => ({}))
    const [error, setError] = useState<Record<string, any>>(() => ({}))

    useEffect(() => {
        setFinish(!error || Object.keys(error).length === 0)
    }, [error])

    return {
        data,
        error,
        finish,
        setFieldValue(name: string, value: any) {
            // functional form: fields commit on blur and would otherwise race a stale closure
            setData(current => ({ ...current, [name]: value }))
        },
        setFieldError(fieldName, msg) {
            setError(current => ({ ...current, [fieldName]: msg }))
        },
        setFieldSuccess(fieldName) {
            setError(current => {
                const next = { ...current }
                delete next[fieldName]
                return next
            })
        },
        clear() {
            setData({})
        },
    }
}

export const Form = (props: PropsWithChildren<{ form: FormInstance, prefix?: string }>) => {
    return (
        <PrefixContext.Provider value={props.prefix ?? "contactus"}>
            <FormContext.Provider value={props.form}>
                <div>{props.children}</div>
            </FormContext.Provider>
        </PrefixContext.Provider>
    )
}

export interface FormItemRuleType {
    accept: (value: string) => boolean
    msg?: string
    show?: boolean
}

export interface FormItemType {
    label: string
    required?: boolean
    name: string
    rules?: FormItemRuleType[]
}

export const FormItem = (props: PropsWithChildren<FormItemType>) => {

    const { setFieldValue, setFieldError, setFieldSuccess, data = {}, error = {} } = useContext(FormContext)
    const prefix = useContext(PrefixContext)

    const _setFieldValue = (value: any) => {
        const rule = props.rules?.find(rule => !rule.accept(value))
        if (rule) {
            setFieldError(props.name, { msg: rule.msg, show: rule.show })
        } else {
            setFieldSuccess(props.name)
        }
        setFieldValue(props.name, value)
    }

    return (
        <FormItemContext.Provider value={{
            name: props.name,
            setFieldValue: _setFieldValue,
            fieldValue: data[props.name],
            fieldError: error[props.name]
        }}>
            <Flex vertical gap={4} reposive={false}>
                <div className={[`${prefix}__form__item__label`, error[props.name]?.["show"] ? `${prefix}__form__item__label_error` : ""].join(" ")}>
                    {props.label}{props.required ? " * " : ""}
                </div>
                <div className={`${prefix}__form__item__content`}>
                    {props.children}
                </div>
                <div className={`${prefix}__form__item__error`}>{error[props.name]?.["msg"] ?? undefined}</div>
            </Flex>
        </FormItemContext.Provider>
    )
}

/** Commits on blur, mirroring the existing contact form's behaviour. */
const useField = () => {
    const { setFieldValue, fieldValue = "", fieldError } = useContext(FormItemContext)
    const [tmp, setTmp] = useState<string>(() => fieldValue)
    const [inputing, setInputing] = useState<boolean>()

    // registers the field so required-rules fail before the user touches it
    useEffect(() => { setFieldValue(undefined) }, [])
    useEffect(() => { setTmp(fieldValue) }, [fieldValue])

    return {
        tmp,
        fieldError,
        onFocus: () => setInputing(true),
        onBlur: () => { setInputing(false); setFieldValue(tmp) },
        onChange: (value: string) => {
            setTmp(value)
            if (!inputing) { setFieldValue(tmp) }
        }
    }
}

export const Input = (props: React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>) => {
    const { tmp, fieldError, onFocus, onBlur, onChange } = useField()
    const prefix = useContext(PrefixContext)

    return (
        <input
            className={[`${prefix}__form__input`, fieldError?.["show"] ? `${prefix}__form__input_error` : ""].join(" ")}
            {...props}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={e => onChange(e.target.value)}
            value={tmp}
        ></input>
    )
}

export const Textarea = (props: React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>) => {
    const { tmp, fieldError, onFocus, onBlur, onChange } = useField()
    const prefix = useContext(PrefixContext)

    return (
        <textarea
            className={[`${prefix}__form__textarea`, fieldError?.["show"] ? `${prefix}__form__textarea_error` : ""].join(" ")}
            {...props}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={e => onChange(e.target.value)}
            value={tmp}
        ></textarea>
    )
}
